import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Users, Search, Filter, AlertTriangle, Lock, CircleDot, PanelLeftClose, PanelLeftOpen } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// DropshipWorkspaceShell
//   Adds the "workspace-split" chrome to the Dropship app:
//     • left rail = "All partners" aggregate + individual partners list
//     • center KPI strip = selected partner rollup (or aggregate)
//     • right = existing 6-tab content (rendered as {children})
//   The active partner id is persisted to URL param `?partner=all|<id>` so
//   deep-links + browser back/forward work naturally. Consumer components
//   read `useDropshipPartner()` to filter their own data — the shell itself
//   does NOT modify or filter anyone's data.
// ─────────────────────────────────────────────────────────────────────────

const HEALTH_TIP = { ok: "Synced", warn: "Warning", error: "Error" };

/** Read the active partner ("all" or partner id) from URL. */
export function useDropshipPartner() {
  const [search] = useSearchParams();
  const raw = search.get("partner") || "all";
  return { partnerId: raw === "all" ? null : raw, isAll: raw === "all" };
}

/**
 * @param {"seller"|"buyer"} role
 * @param {ReactNode}        children     Existing tab strip + tab content.
 * @param {ReactNode}        pageHeader   Existing <PageHeader/> element.
 * @param {ReactNode}        preTabsSlot  Optional slot rendered between the
 *                                        workspace header and the tab strip
 *                                        (e.g. SellerDropshipReadiness).
 */
export default function DropshipWorkspaceShell({ role, children, pageHeader, preTabsSlot }) {
  const [search, setSearch] = useSearchParams();
  const rawPartner = search.get("partner") || "all";
  const isAll = rawPartner === "all";

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Rail collapse — persisted per role so the pref sticks across sessions.
  const railKey = `dropship-rail-collapsed:${role}`;
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem(railKey) === "1"; } catch { return false; }
  });
  const toggleRail = () => {
    setRailCollapsed(v => {
      const next = !v;
      try { localStorage.setItem(railKey, next ? "1" : "0"); } catch { /* ignore quota errors */ }
      return next;
    });
  };

  const partnersLabel = role === "seller" ? "Buyers" : "Suppliers";
  const partnerLabel  = role === "seller" ? "Buyer"  : "Supplier";

  // Fetch partners from the same source of truth used elsewhere in the app.
  // We hit /dropship/summary + /dropship/orders + /buyers or /sellers so
  // the aggregate math matches what existing tabs already display.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [orders, roster] = await Promise.all([
          api.get("/dropship/orders").catch(() => ({ data: [] })),
          // Feb 2026 · Iter 207 · Sellers now see only buyers who
          // (a) signed a dropship agreement AND (b) kept their
          // dropship enabled — matches Faire/Amazon Vendor pattern
          // and avoids cluttering the sidebar with non-participants.
          // Suppliers side unchanged — still uses /sellers roster.
          api.get(role === "seller" ? "/dropship/enrolled-buyers" : "/sellers").catch(() => ({ data: [] })),
        ]);
        if (dead) return;

        // Aggregate order stats per partner.
        const idField = role === "seller" ? "buyer_id" : "seller_id";
        const nameField = role === "seller" ? "buyer_name" : "seller_name";
        const stats = new Map();
        for (const o of orders.data || []) {
          const id = o[idField];
          if (!id) continue;
          const s = stats.get(id) || { orders30d: 0, gmv30d: 0, name: o[nameField], lastActivity: null, needsAction: 0 };
          s.orders30d += 1;
          s.gmv30d += Number(o.total_amount || o.amount || 0);
          if (o.status === "forwarded" || o.status === "awaiting_payment" || o.status === "stalled") s.needsAction += 1;
          stats.set(id, s);
        }

        // Merge with roster (so partners with no orders yet still appear).
        const roll = (roster.data || []).map(r => {
          const s = stats.get(r.id) || { orders30d: 0, gmv30d: 0, needsAction: 0 };
          const health = s.needsAction > 0 ? "warn" : "ok";
          return {
            id: r.id,
            name: r.name || r.business_name || r.email || "(unnamed)",
            orders30d: s.orders30d,
            gmv30d: s.gmv30d,
            needsAction: s.needsAction,
            health,
            sync: s.orders30d > 0 ? "Active" : "Idle",
          };
        }).sort((a, b) => (b.gmv30d - a.gmv30d) || (b.orders30d - a.orders30d));

        setPartners(roll);
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [role]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? partners.filter(p => p.name.toLowerCase().includes(s)) : partners;
  }, [partners, q]);

  const selected = isAll ? null : partners.find(p => p.id === rawPartner);
  const totalOrders  = partners.reduce((n, p) => n + p.orders30d, 0);
  const totalGmv     = partners.reduce((n, p) => n + p.gmv30d, 0);
  const needsAction  = partners.filter(p => p.needsAction > 0).length;
  const healthyCount = partners.filter(p => p.health === "ok").length;

  const pickPartner = (id) => {
    // Read the LATEST URL directly to avoid a useSearchParams closure race
    // with sibling components (tab strip) that also write to search params.
    const next = new URLSearchParams(window.location.search);
    if (id === "all") next.delete("partner");
    else next.set("partner", id);
    setSearch(next, { replace: true });
  };

  return (
    <div data-testid={`${role}-dropship`}>
      {pageHeader}

      {/* preTabsSlot renders BEFORE the workspace-split — used for readiness banners */}
      {preTabsSlot}

      <div className="mt-4 flex gap-0 min-h-[600px] border border-border rounded-2xl overflow-hidden mx-8 mb-8 bg-background">
        {/* LEFT rail — partner list ────────────────────────────────────────── */}
        {!railCollapsed && (
        <aside className="w-56 flex-shrink-0 border-r border-border bg-secondary/20 flex flex-col" data-testid="dropship-workspace-rail">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-background rounded-md px-2.5 py-1.5 border border-border min-w-0">
              <Search className="w-3.5 h-3.5 text-foreground/40 flex-shrink-0" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={`Search ${partnersLabel.toLowerCase()}…`}
                className="text-xs bg-transparent flex-1 outline-none placeholder:text-foreground/40 min-w-0"
                data-testid="dropship-partner-search"
              />
              <Filter className="w-3.5 h-3.5 text-foreground/40 flex-shrink-0" />
            </div>
            <button
              onClick={toggleRail}
              data-testid="dropship-rail-collapse"
              title="Hide sidebar"
              className="w-7 h-7 rounded-md hover:bg-secondary/60 flex items-center justify-center flex-shrink-0 text-foreground/60 hover:text-foreground"
            >
              <PanelLeftClose className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Aggregate "All partners" row — always pinned */}
          <button
            onClick={() => pickPartner("all")}
            data-testid="dropship-partner-all"
            className={`w-full text-left px-4 py-3 border-b border-border/70 hover:bg-secondary/40 transition ${isAll ? "bg-accent/40 border-l-2 border-l-foreground" : "border-l-2 border-l-transparent"}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-foreground text-background flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">All {partnersLabel.toLowerCase()}</span>
                  <span className="text-[10px] text-foreground/40">aggregate</span>
                </div>
                <div className="text-[11px] text-foreground/60 mt-0.5 tabular-nums">
                  {totalOrders} orders · ${totalGmv.toLocaleString()}
                </div>
              </div>
            </div>
          </button>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-xs text-foreground/40">Loading {partnersLabel.toLowerCase()}…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-xs text-foreground/40">
                No {partnersLabel.toLowerCase()} yet.
                {role === "seller" ? " Invite a buyer to start." : " Sign up with a supplier to start."}
              </div>
            ) : (
              <>
                <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-widest text-foreground/40">Individual {partnersLabel.toLowerCase()}</div>
                {filtered.map(p => {
                  const active = rawPartner === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => pickPartner(p.id)}
                      data-testid={`dropship-partner-${p.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-border/70 hover:bg-secondary/40 transition ${active ? "bg-accent/40 border-l-2 border-l-foreground" : "border-l-2 border-l-transparent"}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground/70">
                          {p.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium truncate">{p.name}</span>
                            {p.needsAction > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" title={`${p.needsAction} orders need attention`} />}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[11px] text-foreground/50 tabular-nums">${p.gmv30d.toLocaleString()}</span>
                            <span className="text-foreground/20">·</span>
                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${p.health === "ok" ? "text-emerald-700" : "text-amber-700"}`}>
                              <CircleDot className="w-2 h-2" /> {HEALTH_TIP[p.health]}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>

          <div className="p-3 border-t border-border text-[11px] text-foreground/60 flex items-center justify-between">
            <span>{partners.length} active</span>
            <span className="tabular-nums">${totalGmv.toLocaleString()}</span>
          </div>
        </aside>
        )}

        {/* RIGHT: workspace with header + existing tabs (children) */}
        <main className="flex-1 min-w-0 overflow-hidden flex flex-col">
          {/* Compact workspace header · single slim strip (Feb 2026 · Iter 198).
              Previously a 4-KPI card taking ~230px vertical space — collapsed
              into a ~40px inline strip so the fulfillment table stays above
              the fold on standard viewports. All info is still shown, just
              inline. Full KPI expansion available in Analytics tab. */}
          <div className="px-6 lg:px-8 py-2.5 border-b border-border">
            <div className="flex items-center gap-3 flex-wrap text-[12px]">
              {railCollapsed && (
                <button
                  onClick={toggleRail}
                  data-testid="dropship-rail-expand"
                  title={`Show ${partnersLabel.toLowerCase()} sidebar`}
                  className="w-7 h-7 rounded-md border border-border hover:bg-secondary/60 flex items-center justify-center text-foreground/70 hover:text-foreground shrink-0"
                >
                  <PanelLeftOpen className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[9.5px] mono uppercase tracking-[0.16em] text-foreground/45">
                  {isAll ? "Aggregate" : partnerLabel}
                </span>
                <h2 className="text-[13.5px] font-semibold tracking-tight truncate" data-testid="dropship-workspace-title">
                  {isAll ? `All ${partnersLabel.toLowerCase()}` : (selected?.name || partnerLabel)}
                </h2>
              </div>
              <span className="w-px h-4 bg-border" />
              {isAll ? (
                <>
                  <HealthPill kind="ok">{healthyCount} healthy</HealthPill>
                  {needsAction > 0 && <HealthPill kind="warn">{needsAction} need attention</HealthPill>}
                </>
              ) : selected ? (
                <>
                  <HealthPill kind={selected.health}>{HEALTH_TIP[selected.health]}</HealthPill>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-accent inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" />Agreement locked
                  </span>
                </>
              ) : null}
              <span className="w-px h-4 bg-border" />
              {/* Inline KPI trio (Orders · GMV · Active · Needs action).
                  Compact — no big numbers, just label-value pairs. */}
              <div className="flex items-center gap-4 flex-wrap">
                <InlineKpi label="Orders 30d" value={isAll ? totalOrders : (selected?.orders30d ?? 0)} />
                <InlineKpi label="GMV 30d"    value={`$${(isAll ? totalGmv : (selected?.gmv30d ?? 0)).toLocaleString()}`} />
                <InlineKpi label="Active"     value={isAll ? partners.length : 1} />
                <InlineKpi
                  label="Needs action"
                  value={isAll ? needsAction : (selected?.needsAction ?? 0)}
                  accent={(isAll ? needsAction : (selected?.needsAction ?? 0)) > 0 ? "warn" : undefined}
                />
              </div>
            </div>
          </div>

          {/* Existing tab strip + tab content renders here */}
          <div className="flex-1 min-w-0 overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function KPI({ label, value, hint, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-foreground/50">{label}</div>
      <div className={`text-lg font-semibold tabular-nums mt-0.5 ${accent === "warn" ? "text-amber-600" : ""}`}>{value}</div>
      {hint && <div className="text-[11px] text-foreground/50 mt-0.5">{hint}</div>}
    </div>
  );
}

/** Inline pill-style KPI for the compact workspace header strip.
    Label + value on a single row — designed to save vertical space
    when many KPIs are shown side-by-side in a slim header. */
function InlineKpi({ label, value, accent }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/45">{label}</span>
      <span className={`text-[12.5px] font-semibold tabular-nums ${accent === "warn" ? "text-amber-700" : "text-foreground"}`}>{value}</span>
    </span>
  );
}

function HealthPill({ kind, children }) {
  const map = {
    ok:    "bg-emerald-50 text-emerald-700 ring-emerald-200",
    warn:  "bg-amber-50 text-amber-700 ring-amber-200",
    error: "bg-red-50 text-red-700 ring-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ring-1 ${map[kind] || map.ok}`}>
      <CircleDot className="w-2.5 h-2.5" /> {children}
    </span>
  );
}
